import type { DataSetsProps, DownloadRegisterProps } from '@/types';
import { openDBConnection, toCoded } from '@/talons/dbFunctions/useDBFunctions';

const TABLE = 'download_registers';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const registerKey = (url: string, tipoArquivo: string) => `${url}::${tipoArquivo}`;

const readRegisters = async (): Promise<DownloadRegisterProps[]> => {
  const currDB = await openDBConnection();
  try {
    const storeName = toCoded(TABLE);
    if (!currDB.objectStoreNames.contains(storeName)) return [];

    const transaction = currDB.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const allKeys = await store.getAllKeys();
    const lastKey = allKeys[allKeys.length - 1];
    if (lastKey == null) return [];

    const lastData = await store.get(lastKey as IDBValidKey);
    const data = lastData?.data;
    return Array.isArray(data) ? (data as DownloadRegisterProps[]) : [];
  } catch {
    return [];
  } finally {
    currDB.close();
  }
};

const writeRegisters = async (registers: DownloadRegisterProps[]): Promise<void> => {
  const currDB = await openDBConnection();
  try {
    const storeName = toCoded(TABLE);
    if (!currDB.objectStoreNames.contains(storeName)) return;

    const transaction = currDB.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const allKeys = await store.getAllKeys();
    if (allKeys.length) {
      await store.delete(allKeys[allKeys.length - 1]);
    }
    await store.put({ data: registers, keyPath: storeName });
    await transaction.done;
  } finally {
    currDB.close();
  }
};

const isExpired = (downloadedAt: string, now = Date.now()): boolean => {
  const ts = Date.parse(downloadedAt);
  if (!Number.isFinite(ts)) return true;
  return now - ts > MAX_AGE_MS;
};

const normalizeRegister = (item: DownloadRegisterProps): DownloadRegisterProps | null => {
  const url = item?.url;
  const TIPO_ARQUIVO = item?.TIPO_ARQUIVO || (item as DownloadRegisterProps & { tipoArquivo?: string })?.tipoArquivo;
  if (!url || !TIPO_ARQUIVO) return null;
  return {
    url,
    downloadedAt: item.downloadedAt,
    TIPO_ARQUIVO,
  };
};

export const pruneExpiredDownloadRegisters = async (): Promise<DownloadRegisterProps[]> => {
  const now = Date.now();
  const current = await readRegisters();
  const kept = current
    .map(normalizeRegister)
    .filter((item): item is DownloadRegisterProps => !!item && !isExpired(item.downloadedAt, now));
  if (kept.length !== current.length) {
    await writeRegisters(kept);
  }
  return kept;
};

export const getSuccessfulDownloadRegisters = async (): Promise<DownloadRegisterProps[]> => {
  return pruneExpiredDownloadRegisters();
};

export const registerSuccessfulDownloads = async (
  datasets: Array<Pick<DataSetsProps, 'URL_S3' | 'TIPO_ARQUIVO'>>
): Promise<void> => {
  const incoming = datasets.filter((dataset) => !!dataset?.URL_S3 && !!dataset?.TIPO_ARQUIVO);
  if (!incoming.length) return;

  const nowIso = new Date().toISOString();
  const current = await pruneExpiredDownloadRegisters();
  const byKey = new Map<string, DownloadRegisterProps>();

  current.forEach((item) => {
    const normalized = normalizeRegister(item);
    if (!normalized) return;
    byKey.set(registerKey(normalized.url, normalized.TIPO_ARQUIVO), normalized);
  });

  incoming.forEach((dataset) => {
    byKey.set(registerKey(dataset.URL_S3, dataset.TIPO_ARQUIVO), {
      url: dataset.URL_S3,
      downloadedAt: nowIso,
      TIPO_ARQUIVO: dataset.TIPO_ARQUIVO,
    });
  });

  await writeRegisters(Array.from(byKey.values()));
};

export const removeDownloadRegisters = async (
  datasets: Array<Pick<DataSetsProps, 'URL_S3' | 'TIPO_ARQUIVO'>>
): Promise<void> => {
  const keysToRemove = new Set(
    datasets
      .filter((dataset) => !!dataset?.URL_S3 && !!dataset?.TIPO_ARQUIVO)
      .map((dataset) => registerKey(dataset.URL_S3, dataset.TIPO_ARQUIVO))
  );
  if (!keysToRemove.size) return;

  const current = await pruneExpiredDownloadRegisters();
  const kept = current.filter((item) => {
    const normalized = normalizeRegister(item);
    if (!normalized) return false;
    return !keysToRemove.has(registerKey(normalized.url, normalized.TIPO_ARQUIVO));
  });

  if (kept.length !== current.length) {
    await writeRegisters(kept);
  }
};
